import { CUSTOM_QUESTION_TYPES, type CustomQuestion, type CustomQuestionType } from "../../types";
import { questionIdFromLabel } from "../../utils/customQuestions";

const TYPE_LABELS: Record<CustomQuestionType, string> = {
  text: "Text",
  textarea: "Long text",
  number: "Number",
  select: "Choice",
  date: "Date",
  time: "Time",
  datetime: "Date & time",
};

interface CustomQuestionEditorProps {
  questions: CustomQuestion[];
  onChange: (questions: CustomQuestion[]) => void;
}

/**
 * The entity form's list of questions to ask when logging here: a label, an
 * answer type, whether it's required, and the choices for a "Choice" question.
 * Each question's storage key is fixed the moment it's added, so renaming
 * "Song" to "Which song?" keeps every answer already logged under it.
 */
export function CustomQuestionEditor({ questions, onChange }: CustomQuestionEditorProps) {
  const update = (index: number, patch: Partial<CustomQuestion>) =>
    onChange(questions.map((q, i) => (i === index ? { ...q, ...patch } : q)));

  const add = () =>
    onChange([
      ...questions,
      // Id is assigned from the label on save (`normalizeCustomQuestions`);
      // a blank one here marks the row as not yet keyed.
      { id: "", label: "", type: "text", required: false, order: questions.length + 1 },
    ]);

  const remove = (index: number) => onChange(questions.filter((_, i) => i !== index));

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= questions.length) return;
    const next = [...questions];
    const [q] = next.splice(index, 1);
    next.splice(target, 0, q!);
    onChange(next);
  };

  // A label typed on a brand-new row keys the question right away, so the
  // hint under the row shows where answers will be stored before saving.
  const setLabel = (index: number, label: string) => {
    const q = questions[index]!;
    const patch: Partial<CustomQuestion> = { label };
    if (!q.id) {
      patch.id = label.trim()
        ? questionIdFromLabel(label, questions.filter((_, i) => i !== index).map((o) => o.id))
        : "";
    }
    update(index, patch);
  };

  const setType = (index: number, type: CustomQuestionType) => {
    const q = questions[index]!;
    update(index, {
      type,
      options: type === "select" ? q.options ?? [] : undefined,
    });
  };

  return (
    <div className="form-field">
      <span className="form-label">Questions (asked when logging here)</span>
      <span className="form-hint">
        Extra fields for activities at this place — the song on a ride that picks one, the
        booth at a festival stand, the row you sat in. Answers show on the activity and in
        this place's history.
      </span>
      {questions.length > 0 && (
        <div className="event-option-list custom-question-list">
          {questions.map((q, index) => (
            <div key={index} className="event-option-item custom-question-item">
              <div className="event-option-row">
                <input
                  className="form-input event-option-name"
                  value={q.label}
                  placeholder="Question (e.g. Song, Row number)"
                  aria-label="Question label"
                  onChange={(e) => setLabel(index, e.target.value)}
                />
                <select
                  className="form-input event-option-type"
                  value={q.type}
                  aria-label="Answer type"
                  onChange={(e) => setType(index, e.target.value as CustomQuestionType)}
                >
                  {CUSTOM_QUESTION_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {TYPE_LABELS[t]}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="row-x"
                  onClick={() => remove(index)}
                  aria-label="Remove question"
                >
                  ×
                </button>
              </div>
              {q.type === "select" && (
                <textarea
                  className="form-input custom-question-options"
                  value={(q.options ?? []).join("\n")}
                  placeholder={"One choice per line\nConga\nSeptember"}
                  aria-label="Choices, one per line"
                  rows={Math.min(6, Math.max(2, (q.options ?? []).length + 1))}
                  onChange={(e) => update(index, { options: e.target.value.split("\n") })}
                />
              )}
              <div className="custom-question-meta">
                <label className="custom-question-required">
                  <input
                    type="checkbox"
                    checked={q.required}
                    onChange={(e) => update(index, { required: e.target.checked })}
                  />
                  Required
                </label>
                {q.id && (
                  <span className="form-hint custom-question-key" title="Where answers are stored">
                    key: {q.id}
                  </span>
                )}
                <span className="custom-question-order">
                  <button
                    type="button"
                    className="btn-link"
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    aria-label="Move question up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="btn-link"
                    onClick={() => move(index, 1)}
                    disabled={index === questions.length - 1}
                    aria-label="Move question down"
                  >
                    ↓
                  </button>
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="event-option-actions">
        <button type="button" className="btn btn-small btn-secondary" onClick={add}>
          + Add question
        </button>
      </div>
    </div>
  );
}
